import http from '@/api/http'
import type { InstitutionFunction, StoredFormatInstitution } from '@/types/formatInstitution.ts'
import type { UploadedMediaValue } from '@/types/global.ts'
import type { StrapiResponse } from '@/types/strapi.ts'

type EditableInstitutionFields = {
    function: InstitutionFunction
    name: string
    url?: string | null
    logo: UploadedMediaValue
    participatesInSelection?: boolean
}

export type CreateFormatInstitutionData = EditableInstitutionFields & {
    format: string
}

export type UpdateFormatInstitutionData = Partial<EditableInstitutionFields>

export function normalizeMediaRelation(media: UploadedMediaValue): number | null {
    if (typeof media === 'number') return media
    if (media && typeof media === 'object') return media.id ?? null
    return null
}

function mapCreatePayload(data: CreateFormatInstitutionData) {
    return {
        format: data.format,
        function: data.function,
        name: data.name,
        url: data.url ?? null,
        logo: normalizeMediaRelation(data.logo),
        participatesInSelection: data.participatesInSelection ?? false,
    }
}

function mapUpdatePayload(data: UpdateFormatInstitutionData) {
    // Omit undefined fields so partial updates preserve stored Strapi values.
    return {
        ...(data.function !== undefined ? { function: data.function } : {}),
        ...(data.name !== undefined ? { name: data.name } : {}),
        ...(data.url !== undefined ? { url: data.url } : {}),
        ...(data.logo !== undefined ? { logo: normalizeMediaRelation(data.logo) } : {}),
        ...(data.participatesInSelection !== undefined
            ? { participatesInSelection: data.participatesInSelection }
            : {}),
    }
}

export async function getInstitutionsByFormat(
    formatDocumentId: string
): Promise<StoredFormatInstitution[]> {
    const response = await http.get<StrapiResponse<StoredFormatInstitution[]>>(
        '/format-institutions',
        {
            params: {
                filters: {
                    format: {
                        documentId: {
                            $eq: formatDocumentId,
                        },
                    },
                },
                populate: ['logo'],
                sort: 'position:asc',
            },
        }
    )
    return response.data.data
}

export async function createInstitution(
    institutionData: CreateFormatInstitutionData
): Promise<StoredFormatInstitution> {
    const response = await http.post<StrapiResponse<StoredFormatInstitution>>(
        '/format-institutions',
        { data: mapCreatePayload(institutionData) },
        { params: { populate: ['logo'] } }
    )
    return response.data.data
}

export async function updateInstitution(
    documentId: string,
    institutionData: UpdateFormatInstitutionData
): Promise<StoredFormatInstitution> {
    const response = await http.put<StrapiResponse<StoredFormatInstitution>>(
        `/format-institutions/${documentId}`,
        { data: mapUpdatePayload(institutionData) },
        { params: { populate: ['logo'] } }
    )
    return response.data.data
}

export async function deleteInstitution(documentId: string): Promise<void> {
    await http.delete(`/format-institutions/${documentId}`)
}
